import axios from "@utils/axios-instance";
import type {
  TConversationFilters,
  TInboxConversation,
  TInboxConversationStatus,
  TInboxMessage,
} from "@/models/inbox.model";

/** Omnichannel Inbox (IR-69, replies IR-70) — `/inbox/*`. */

export async function getConversations(
  filters: TConversationFilters,
  before?: string
): Promise<{ conversations: TInboxConversation[]; nextCursor?: string }> {
  const params = new URLSearchParams();
  if (!filters.showResolved) params.set("status", "open");
  if (filters.channel !== "all") params.set("channel", filters.channel);
  if (filters.assigned !== "all") params.set("assigned", filters.assigned);
  if (filters.search.trim()) params.set("search", filters.search.trim());
  if (before) params.set("before", before);
  const { data } = await axios.get(`/inbox/conversations?${params.toString()}`);
  return data;
}

export async function getConversation(id: number): Promise<TInboxConversation> {
  const { data } = await axios.get<TInboxConversation>(`/inbox/conversations/${id}`);
  return data;
}

export async function getMessages(
  conversationId: number,
  before?: number
): Promise<{ messages: TInboxMessage[]; hasMore: boolean }> {
  const qs = before ? `?before=${before}` : "";
  const { data } = await axios.get(`/inbox/conversations/${conversationId}/messages${qs}`);
  return data;
}

export async function markConversationRead(id: number): Promise<TInboxConversation> {
  const { data } = await axios.post<TInboxConversation>(`/inbox/conversations/${id}/read`);
  return data;
}

export async function setConversationStatus(
  id: number,
  status: TInboxConversationStatus
): Promise<TInboxConversation> {
  const { data } = await axios.patch<TInboxConversation>(`/inbox/conversations/${id}`, { status });
  return data;
}

export async function assignConversation(id: number, userId: number | null): Promise<TInboxConversation> {
  const { data } = await axios.put<TInboxConversation>(`/inbox/conversations/${id}/assignee`, { userId });
  return data;
}

/**
 * The stored reply: `sent`, or `failed` with a reason when the channel refused
 * it. `clientKey` is the Idempotency-Key SMS replies to a lead honour: a retry
 * with the same key never texts twice (other channels ignore it).
 */
export async function sendTextReply(conversationId: number, body: string, clientKey?: string): Promise<TInboxMessage> {
  const { data } = await axios.post<TInboxMessage>(
    `/inbox/conversations/${conversationId}/messages`,
    { body },
    clientKey ? { headers: { "Idempotency-Key": clientKey } } : undefined
  );
  return data;
}

export async function sendImageReply(conversationId: number, file: File, caption?: string): Promise<TInboxMessage> {
  const form = new FormData();
  form.append("file", file);
  if (caption) form.append("caption", caption);
  const { data } = await axios.post<TInboxMessage>(`/inbox/conversations/${conversationId}/messages/image`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/**
 * Compose a text to a phone number, sent from the caller's OWN caller ID (their
 * line, else the company line): the Inbox "New message", and the way around a
 * teammate's personal thread. Answers the conversation the text landed in
 * (null only if the text went but its Inbox copy could not be stored).
 */
export async function composeSms(
  /** Who to text: a raw phone number, or a lead (the backend uses the lead's stored phone). */
  to: { contactHandle: string } | { leadId: number },
  body: string,
  idempotencyKey: string
): Promise<TInboxConversation | null> {
  const { data } = await axios.post<{ conversation: TInboxConversation | null }>(
    "/inbox/sms/messages",
    { ...to, body },
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
  return data.conversation;
}

/** A message's image, fetched with the user's token (an <img src> can't send it). */
export async function getMessageMedia(messageId: number): Promise<Blob> {
  const { data } = await axios.get<Blob>(`/inbox/messages/${messageId}/media`, { responseType: "blob" });
  return data;
}

/** Company-wide unread messages, and open conversations nobody is assigned to. */
export async function getUnreadCount(): Promise<{ count: number; unassigned: number }> {
  const { data } = await axios.get<{ count: number; unassigned?: number }>("/inbox/unread-count");
  return { count: data.count, unassigned: data.unassigned ?? 0 };
}
